import { useCallback, useEffect, useMemo, useReducer } from "react";

import { useAtom, useAtomValue, atom, Atom, useSetAtom, useStore } from "jotai";

import { ArrayDataSource } from "../../dataView";
import { assertNever } from "../../obj";
import {
    excludeSplitAtomFormValidationError,
    SplitFormAtom,
} from "../splitFormAtom";

import {
    CompoundFormState,
    FormAtom,
    Store,
    UseCompoundFormStateArgs,
    UseFormAtomReturn,
    ValidationResult,
} from "./types";

/**
 * Helper function used to retrieve all important information from a FormAtom so a form field can properly render
 * @param atom the form atom
 */
export function useFormAtom<T>(atom: FormAtom<T>): UseFormAtomReturn<T> {
    const { fieldStateAtom, initialValueAtom, areEqual, schema } = atom;
    const [value, dispatchValue] = useAtom(atom);
    const [fieldState, setFieldState] = useAtom(fieldStateAtom);
    const initialValue = useAtomValue(initialValueAtom);

    const setCacheValue = useCallback(
        (newValue: Awaited<T>) =>
            setFieldState(() => ({
                type: "CACHED",
                value: newValue,
                dirty:
                    initialValue.defined &&
                    !areEqual(initialValue.value, newValue),
            })),
        [setFieldState, initialValue, areEqual]
    );

    return {
        value,
        schema,
        dirty: fieldState.dirty,
        error:
            fieldState.type === "ERRORED"
                ? excludeSplitAtomFormValidationError(fieldState.error)
                : undefined,
        touched: fieldState.type !== "NOT_TOUCHED",
        validating: fieldState.type === "VALIDATING",
        dispatching: fieldState.type === "DISPATCHING",
        setCacheValue,
        dispatchValue,
    };
}

/**
 * Helper hook used to get some insights on the current state of a form atom collection.
 * Requires formAtoms to follow this naming convention:
 *  - Atoms of enums of form atoms / undefined => "AtomAtom" suffix
 *  - Atoms of an array of atoms => "AtomsAtom" suffix
 *  - regular form atoms=> "Atom" suffix
 *
 *  If typescript complains about a missing 3rd parameter, it means we failed to identify atoms based on their names
 *
 *  How to read the error message:
 *  Typescript will ask for a 3rd parameter, representing the union of all the errors
 *  Each error is a tuple of the prefix as it was parsed, the matching type and an error message, i.e.
 *  > TS2554: Expected 3 arguments, but got 2.
 *
 *  Then I pass a dummy third value:
 *  > TS2345: Argument of type 'number' is not assignable to parameter of type '["myObjectAto", FormAtom , "Unknown key format, expected Atom, AtomAtom or AtomsAtom suffix"] | ["myList", Atom  []>, "Expected FormAtom "]'.
 *
 * @param formAtoms
 * @param baseAtom
 */
export function useCompoundFormState<FormAtoms, Base>(
    ...[formAtoms, baseAtom]: UseCompoundFormStateArgs<FormAtoms, Base>
): CompoundFormState<Base> {
    const { atomAtoms, atomsAtoms, atoms } = useMemo(() => {
        const atomAtoms: Atom<FormAtom<unknown> | undefined>[] = [];
        const atomsAtoms: Atom<FormAtom<unknown>[]>[] = [];
        const atoms: FormAtom<unknown>[] = [];

        for (const [key, atom] of Object.entries(
            formAtoms as { [K in string]: Atom<unknown> }
        )) {
            if (key.endsWith("AtomAtom")) {
                atomAtoms.push(atom as Atom<FormAtom<unknown> | undefined>);
            } else if (key.endsWith("AtomsAtom")) {
                atomsAtoms.push(atom as Atom<FormAtom<unknown>[]>);
            } else if (key.endsWith("Atom")) {
                atoms.push(atom as FormAtom<unknown>);
            } else {
                throw new Error(
                    `Unknown key format, expected Atom, AtomAtom or AtomsAtom suffix, got ${key}`
                );
            }
        }

        return {
            atomAtoms,
            atomsAtoms,
            atoms,
        };
    }, [formAtoms]);

    const allFormAtomsAtom: Atom<FormAtom<unknown>[]> = useMemo(
        () =>
            atom((get) => {
                return [
                    ...atoms,
                    ...atomsAtoms.flatMap((atomsAtom) => get(atomsAtom)),
                    ...atomAtoms.reduce(
                        (activeAtoms: FormAtom<unknown>[], atomAtom) => {
                            const atom = get(atomAtom);
                            if (atom !== undefined) {
                                return [...activeAtoms, atom];
                            } else {
                                return activeAtoms;
                            }
                        },
                        []
                    ),
                ];
            }),
        [atomAtoms, atomsAtoms, atoms]
    );

    // dirty
    // The form is dirty as long as one of the form atoms is dirty
    const dirtyAtom = useMemo(
        () =>
            atom((get) =>
                get(allFormAtomsAtom).some(
                    (formAtom) => get(formAtom.fieldStateAtom).dirty
                )
            ),
        [allFormAtomsAtom]
    );
    const dirty = useAtomValue(dirtyAtom);

    // validate
    // Triggers a validation on all fields, collecting all errors,
    // and if everything is valid, read from baseAtom
    const validateAtom = useMemo(
        () =>
            atom(
                null,
                async (
                    get,
                    set,
                    forceRevalidation?: boolean
                ): Promise<ValidationResult<Base>> => {
                    const results = await Promise.all(
                        get(allFormAtomsAtom).map(async (formAtom) =>
                            set(
                                formAtom.validateFieldAtom,
                                forceRevalidation ?? false
                            )
                        )
                    );
                    const aggregatedResult = results.reduce(
                        (acc, result) => {
                            switch (result.type) {
                                case "ERROR":
                                    return {
                                        type: "ERROR",
                                        errors: [
                                            ...(acc.type === "ERROR"
                                                ? acc.errors
                                                : []),
                                            ...result.errors,
                                        ],
                                    };
                                case "SUPERSEDED":
                                    return acc.type === "ERROR"
                                        ? acc
                                        : { type: "SUPERSEDED" };
                                case "SUCCESS":
                                    return acc;
                                default:
                                    return assertNever(result);
                            }
                        },
                        {
                            type: "SUCCESS",
                            values: undefined,
                        }
                    );

                    if (aggregatedResult.type === "SUCCESS") {
                        return {
                            type: "SUCCESS",
                            values: get(baseAtom),
                        };
                    }
                    return aggregatedResult;
                }
            ),
        [allFormAtomsAtom, baseAtom]
    );
    const validate = useSetAtom(validateAtom);

    // reset
    // Triggers a reset on all fields, waiting for all propagation promises to complete
    const resetFieldsAtom = useMemo(
        () =>
            atom(null, async (get, set) => {
                await Promise.all(
                    get(allFormAtomsAtom).map(async (formAtom) =>
                        set(formAtom.resetFieldAtom)
                    )
                );
            }),
        [allFormAtomsAtom]
    );
    const reset = useSetAtom(resetFieldsAtom);

    return {
        dirty,
        validate,
        reset,
    };
}

/**
 * Subscribe to changes on both dataSourceAtomsAtom and individual form atoms.
 * Creates an array datasource based on dataSourceAtomsAtom value (an `Array<FormAtom<Item>>`).
 *
 * @param dataSourceAtomsAtom
 * @param getId
 */
export function useArrayAtomDataSource<Item>(
    dataSourceAtomsAtom: SplitFormAtom<Item>,
    getId?: (item: Item) => string
): [ArrayDataSource<FormAtom<Item>>, Store] {
    const store = useStore();

    // Force a rerender when the array structure changes (insert, delete, move)
    const dataSourceAtoms = useAtomValue(dataSourceAtomsAtom);

    // Force a rerender when any row state changes.
    // Having better granularity would be a plus, but that isn't possible without a datatable rewrite.
    const [, rerender] = useReducer(
        (i) => i + 1,
        undefined,
        () => 0
    );
    useEffect(() => {
        const unsubs = dataSourceAtoms.flatMap((dataSourceAtom) =>
            store.sub(dataSourceAtom.fieldStateAtom, rerender)
        );
        return () => unsubs.forEach((unsub) => unsub());
    }, [dataSourceAtoms, store, rerender]);

    const arrayDataSource = useMemo(
        () =>
            new ArrayDataSource(dataSourceAtoms, (atom) =>
                getId ? getId(store.get(atom)) : atom.toString()
            ),
        [dataSourceAtoms, getId, store]
    );
    return [arrayDataSource, store];
}
